import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Users, Clock, ArrowRightLeft, DollarSign, ArrowLeft, ArrowRight, Calendar, Banknote, Shield, Building2, UserPlus, UserMinus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid, Legend, PieChart, Pie, ComposedChart, ReferenceLine } from "recharts";
import api, { handleApiError, formatCurrency, formatDateTime, brMonthStr } from "@/lib/api";
import { toast } from "sonner";
import MFASetup from "@/components/MFASetup";
import GettingStarted from "@/components/GettingStarted";
import { useSSE } from "@/hooks/useSSE";

const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

export default function CompanyDashboard() {
  const { user } = useAuth();
  const brMonth = brMonthStr(); // "YYYY-MM" in BRT
  const [year, setYear] = useState(Number(brMonth.split("-")[0]));
  const [month, setMonth] = useState(Number(brMonth.split("-")[1]));
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [deptData, setDeptData] = useState([]);
  const [turnover, setTurnover] = useState(null);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const monthStr = `${year}-${String(month).padStart(2, "0")}`;
    try {
      const [statsRes, recentRes, deptRes, turnoverRes, payrollRes] = await Promise.all([
        api.get(`/admin/stats-by-month?month=${monthStr}`),
        api.get("/anticipations?page=1&page_size=5"),
        api.get(`/admin/cost-by-department?month=${monthStr}`),
        api.get("/admin/turnover?months=12"),
        api.get("/admin/payroll-history?months=12"),
      ]);
      setStats(statsRes.data);
      const recentData = recentRes.data;
      setRecent((recentData.items || recentData).slice(0, 5));
      setDeptData(deptRes.data.departments || []);
      setTurnover(turnoverRes.data);
      setPayrollHistory(payrollRes.data.months || []);
    } catch (err) { toast.error(handleApiError(err, "Erro ao carregar dados do dashboard")); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useSSE(useCallback((event) => {
    if (event === "dashboard_update") fetchData();
  }, [fetchData]));

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>
  );

  const cards = [
    { label: "Colaboradores", value: stats?.total_employees || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Folha Mensal", value: formatCurrency(stats?.folha_mensal), icon: Banknote, color: "text-cyan-400", bg: "bg-cyan-500/10", isText: true },
    { label: "Volume Antecipado", value: formatCurrency(stats?.total_bruto), icon: ArrowRightLeft, color: "text-emerald-400", bg: "bg-emerald-500/10", isText: true },
    { label: "Aprovações Pendentes", value: stats?.pending_approvals || 0, icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10" },
  ];

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="company-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Bem-vindo, {user?.name}</p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-1.5 self-start">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid="dashboard-prev-month">
            <ArrowLeft className="w-3.5 h-3.5" />
          </Button>
          <div className="flex items-center gap-1.5 min-w-[140px] justify-center">
            <Calendar className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-semibold">{MONTHS[month - 1]} {year}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid="dashboard-next-month">
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {user?.role === "company_admin" && !user?.mfa_enabled && (
        <div className={`p-4 rounded-xl border ${user?.mfa_blocked ? "bg-red-500/15 border-red-500/30" : "bg-red-500/10 border-red-500/20"}`} data-testid="mfa-required-banner">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-400">
                {user?.mfa_blocked ? "Acesso bloqueado — 2FA obrigatório" : "Autenticação de dois fatores obrigatória"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {user?.mfa_blocked
                  ? "O prazo de 15 dias para ativação do 2FA expirou. Configure abaixo para desbloquear o acesso à plataforma."
                  : user?.mfa_grace_days_left != null
                    ? `Você tem ${user.mfa_grace_days_left} dia${user.mfa_grace_days_left !== 1 ? "s" : ""} para ativar o 2FA. Após o prazo, o acesso será bloqueado.`
                    : "Para a segurança da sua empresa, a ativação do 2FA é obrigatória."
                }
              </p>
            </div>
          </div>
          <div className="mt-3">
            <MFASetup />
          </div>
        </div>
      )}

      {user?.mfa_blocked ? (
        <div className="space-y-4" data-testid="mfa-blocked-overlay">
          <MFASetup />
          <p className="text-sm text-muted-foreground text-center">Ative o 2FA acima para desbloquear todas as funcionalidades</p>
        </div>
      ) : (
      <>
      <GettingStarted stats={stats} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 stagger-children" data-testid="company-dashboard-stats">
        {cards.map((c) => (
          <Card key={c.label} className="border-white/5 animate-fade-in card-interactive">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
                <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}>
                  <c.icon className={`w-4 h-4 ${c.color}`} />
                </div>
              </div>
              <p className={`font-bold tracking-tight ${c.isText ? "text-xl" : "text-2xl"}`}>
                {c.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {payrollHistory.length > 0 && (() => {
        const latest = payrollHistory[payrollHistory.length - 1];
        const previous = payrollHistory.length > 1 ? payrollHistory[payrollHistory.length - 2] : null;
        const variation = previous && previous.total > 0 ? ((latest.total - previous.total) / previous.total * 100).toFixed(1) : null;
        const totalHeadcount = latest?.headcount || 0;
        const chartData = payrollHistory.map((p, i) => {
          const prev = i > 0 ? payrollHistory[i - 1] : null;
          const varPct = prev && prev.total > 0 ? parseFloat(((p.total - prev.total) / prev.total * 100).toFixed(1)) : 0;
          return {
            ...p,
            despesas_acessorias: p.despesas_acessorias > 0 ? p.despesas_acessorias : null,
            variation_pct: varPct,
          };
        });
        return (
        <Card className="border-white/5" data-testid="payroll-history-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Banknote className="w-4 h-4 text-cyan-400" /> Evolução da Folha — Últimos 12 meses
              </CardTitle>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10">
                  <Banknote className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-blue-400 font-semibold">{formatCurrency(latest?.total || 0)}</span>
                  <span className="text-muted-foreground">atual</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-500/10">
                  <Users className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-cyan-400 font-semibold">{totalHeadcount}</span>
                  <span className="text-muted-foreground">colaboradores</span>
                </div>
                {variation !== null && (
                  <div className={`px-2.5 py-1 rounded-lg ${parseFloat(variation) > 0 ? "bg-yellow-500/10" : "bg-emerald-500/10"}`}>
                    <span className={`font-semibold ${parseFloat(variation) > 0 ? "text-yellow-400" : "text-emerald-400"}`}>
                      {parseFloat(variation) > 0 ? "+" : ""}{variation}%
                    </span>
                    <span className="text-muted-foreground ml-1">vs mês anterior</span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] mt-2">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <ComposedChart data={chartData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month_label" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: "rgba(245,158,11,0.5)", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <ReTooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(value, name) => [name === "variation_pct" ? `${value}%` : formatCurrency(value), name === "salary" ? "Salários" : name === "variation_pct" ? "Variação" : "Desp. Acessórias"]}
                    labelFormatter={(label) => {
                      const item = chartData.find(p => p.month_label === label);
                      return item ? `${label} · ${item.headcount} colaboradores` : label;
                    }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const item = chartData.find(p => p.month_label === label);
                      const salary = payload.find(p => p.dataKey === "salary")?.value || 0;
                      const despesas = payload.find(p => p.dataKey === "despesas_acessorias")?.value || 0;
                      const variation = payload.find(p => p.dataKey === "variation_pct")?.value;
                      const total = salary + despesas;
                      return (
                        <div style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                          <p style={{ color: "#fff", fontWeight: 600, marginBottom: 6 }}>{label}{item ? ` · ${item.headcount} colab.` : ""}</p>
                          <p style={{ color: "#3b82f6", margin: "3px 0" }}>Salários: {formatCurrency(salary)}</p>
                          <p style={{ color: "#a855f7", margin: "3px 0" }}>Desp. Acessórias: {formatCurrency(despesas)}</p>
                          <p style={{ color: "#fff", fontWeight: 700, margin: "6px 0 0", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 6 }}>Total: {formatCurrency(total)}</p>
                          {variation !== undefined && variation !== null && <p style={{ color: "rgba(245,158,11,0.8)", margin: "3px 0 0" }}>Variação: {variation > 0 ? "+" : ""}{variation}%</p>}
                        </div>
                      );
                    }}
                  />
                  <Bar yAxisId="left" dataKey="salary" fill="#3b82f6" radius={[4, 4, 0, 0]} name="salary" barSize={18} />
                  <Bar yAxisId="left" dataKey="despesas_acessorias" fill="#a855f7" radius={[4, 4, 0, 0]} name="despesas_acessorias" barSize={18} />
                  <Line yAxisId="right" type="monotone" dataKey="variation_pct" stroke="rgba(245,158,11,0.5)" strokeWidth={2} dot={false} name="variation_pct" />
                  <ReferenceLine yAxisId="right" y={0} stroke="rgba(255,255,255,0.1)" strokeDasharray="4 4" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-5 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-blue-500" />Salários</div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-purple-500" />Desp. Acessórias</div>
              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 rounded bg-yellow-400" />Variação %</div>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {deptData.length > 0 && (() => {
        const DEPT_COLORS = ["#3b82f6", "#06b6d4", "#a855f7", "#f59e0b", "#10b981", "#ef4444", "#ec4899", "#f97316"];
        const grandTotal = deptData.reduce((s, d) => s + d.total, 0);
        return (
        <Card className="border-white/5" data-testid="cost-by-department">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Custo por Departamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex items-center justify-center">
                <div className="relative w-[220px] h-[220px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <PieChart>
                      <Pie data={deptData} dataKey="total" nameKey="department" cx="50%" cy="50%"
                        innerRadius={65} outerRadius={100} paddingAngle={2} strokeWidth={0}>
                        {deptData.map((_, i) => <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />)}
                      </Pie>
                      <ReTooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, padding: "10px 14px", zIndex: 50 }}
                        wrapperStyle={{ zIndex: 50 }}
                        formatter={(value, name, props) => {
                          const entry = props.payload;
                          return [
                            <span key="v" className="text-white">
                              {formatCurrency(entry.total)}
                              <span style={{ display: "block", fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                                Salários: {formatCurrency(entry.salary)} · Desp. Acess.: {formatCurrency(entry.despesas_acessorias)}
                              </span>
                            </span>,
                            name
                          ];
                        }}
                        labelStyle={{ color: "#fff", fontWeight: 600, marginBottom: 4 }}
                        itemStyle={{ color: "#94a3b8" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                    <p className="text-lg font-bold">{formatCurrency(grandTotal)}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {deptData.map((d, i) => {
                  const pct = grandTotal > 0 ? (d.total / grandTotal * 100).toFixed(1) : 0;
                  return (
                    <div key={d.department} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium truncate">{d.department}</span>
                          <span className="text-sm font-semibold ml-2">{formatCurrency(d.total)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-muted-foreground">{d.headcount} colab. · média {formatCurrency(d.headcount > 0 ? d.total / d.headcount : 0)}</span>
                          <span className="text-xs text-muted-foreground">{pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {turnover && turnover.months?.length > 0 && (
        <Card className="border-white/5" data-testid="turnover-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-400" /> Turnover — Últimos 12 meses
              </CardTitle>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10">
                  <UserPlus className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 font-semibold">{turnover.summary.total_admissions}</span>
                  <span className="text-muted-foreground">admissões</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10">
                  <UserMinus className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-red-400 font-semibold">{turnover.summary.total_terminations}</span>
                  <span className="text-muted-foreground">rescisões</span>
                </div>
                <div className="px-2.5 py-1 rounded-lg bg-yellow-500/10">
                  <span className="text-yellow-400 font-semibold">{turnover.summary.annual_turnover_rate}%</span>
                  <span className="text-muted-foreground ml-1">turnover anual</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] mt-2">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <BarChart data={turnover.months} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month_label" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(value, name) => [value, name === "admissions" ? "Admissões" : name === "terminations" ? "Rescisões" : "Headcount"]}
                  />
                  <Bar dataKey="admissions" fill="#10b981" radius={[4, 4, 0, 0]} name="admissions" />
                  <Bar dataKey="terminations" fill="#ef4444" radius={[4, 4, 0, 0]} name="terminations" />
                  <Line type="monotone" dataKey="headcount" stroke="#60a5fa" strokeWidth={2} dot={false} name="headcount" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-5 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-emerald-500" />Admissões</div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-red-500" />Rescisões</div>
              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 rounded bg-blue-400" />Headcount</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-white/5" data-testid="recent-anticipations">
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Antecipações Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma antecipação registrada</p>
          ) : (
            <div className="space-y-3">
              {recent.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{a.employee_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDateTime(a.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-sm font-semibold">{formatCurrency(a.total_bruto)}</p>
                    <StatusBadge status={a.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <MFASetup />
      </>
      )}
    </div>
  );
}
